import React, { useEffect, useState, useRef } from 'react';
import { ref, get } from 'firebase/database';
import { db } from '../config/firebase';
import './StandaloneApp.css';

interface PublishedApp {
  appId: string;
  name: string;
  emoji: string;
  code: string;
  css: string;
  authorId: string;
  authorHandle: string;
  publishedAt: number;
  description: string;
  format?: string;
  html?: string;
}

interface StandaloneAppProps {
  appId: string;
}

// Real sandbox: no allow-same-origin (apps run on an opaque origin and cannot
// touch inorbit.fm cookies/storage/DOM). Downloads, popups, forms, modals and
// pointer lock are allowed so apps behave like normal pages.
const SANDBOX_PERMS = 'allow-scripts allow-downloads allow-popups allow-forms allow-modals allow-pointer-lock';

const StandaloneApp: React.FC<StandaloneAppProps> = ({ appId }) => {
  const [app, setApp] = useState<PublishedApp | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const hasRendered = useRef(false);

  // Load the published app from Firebase
  useEffect(() => {
    const loadApp = async () => {
      try {
        const appRef = ref(db, `publishedApps/${appId}`);
        const snapshot = await get(appRef);

        if (snapshot.exists()) {
          const appData = snapshot.val();
          setApp({
            appId,
            ...appData
          });
        } else {
          setError('App not found');
        }
      } catch (err) {
        console.error('Error loading app:', err);
        setError('Failed to load app');
      } finally {
        setLoading(false);
      }
    };

    loadApp();
  }, [appId]);

  // Render app when loaded - only once
  useEffect(() => {
    if (!app || !iframeRef.current || hasRendered.current) return;
    hasRendered.current = true;

    // Per-app storage, owned by the parent page (the sandboxed origin has no localStorage)
    const storagePrefix = `orbit_standalone_${appId}_`;
    const storeSnapshot: Record<string, any> = {};
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.indexOf(storagePrefix) === 0) {
          try {
            storeSnapshot[key.slice(storagePrefix.length)] = JSON.parse(localStorage.getItem(key) || 'null');
          } catch (e) { /* skip unparseable */ }
        }
      }
    } catch (e) { /* localStorage unavailable */ }
    const encodedStore = btoa(unescape(encodeURIComponent(JSON.stringify(storeSnapshot))));

    // html-v2 apps: one self-contained HTML file, rendered verbatim with a
    // standalone orbit shim (music/apps no-ops; storage bridged to the parent)
    const standaloneShim = `<script>
(function() {
  var noop = function() { console.warn('[orbit] disabled in standalone mode'); };
  var storeData = {};
  try { storeData = JSON.parse(decodeURIComponent(escape(atob('${encodedStore}')))) || {}; } catch (e) { storeData = {}; }
  window.orbit = {
    music: { play: noop, pause: noop, toggle: noop, next: noop, prev: noop, playTrack: noop,
      getCurrentTrack: function() { return null; }, isPlaying: function() { return false; } },
    apps: { openMusicPlayer: noop, openPhotoBooth: noop, openGarden: noop, openGuestbook: noop,
      openOrbitTV: noop, openGenius: noop, openFileBrowser: noop },
    user: { handle: null, isLoggedIn: false },
    notify: function(msg) { console.log('[orbit notification]', msg); },
    storage: {
      get: function(key) { return Object.prototype.hasOwnProperty.call(storeData, key) ? storeData[key] : null; },
      set: function(key, value) {
        storeData[key] = value;
        window.parent.postMessage({ type: 'sdk', action: 'storage.set', data: { key: key, value: value } }, '*');
      },
      clear: function() {
        storeData = {};
        window.parent.postMessage({ type: 'sdk', action: 'storage.clear' }, '*');
      }
    }
  };
})();
<` + `/script>`;

    // Encode code and CSS as base64 to avoid escaping issues (jsx format)
    const encodedCode = btoa(unescape(encodeURIComponent(app.code || '')));
    const encodedCss = btoa(unescape(encodeURIComponent(app.css || '')));

    // Create iframe content with Babel loaded from CDN
    const jsxContent = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <script src="https://unpkg.com/react@18/umd/react.production.min.js"><` + `/script>
  <script src="https://unpkg.com/react-dom@18/umd/react-dom.production.min.js"><` + `/script>
  <script src="https://unpkg.com/@babel/standalone@7.26.4/babel.min.js"><` + `/script>
  <style id="app-styles"></style>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: -apple-system, BlinkMacSystemFont, 'Geneva', sans-serif;
      background: #1a1a1a;
      color: #ffffff;
      min-height: 100vh;
    }
  </style>
</head>
<body>
  <div id="root"></div>
  <script id="app-code" type="text/plain" data-code="${encodedCode}" data-css="${encodedCss}" data-store="${encodedStore}"><` + `/script>
  <script>
    // Decode and inject CSS
    (function() {
      var codeEl = document.getElementById('app-code');
      var cssEncoded = codeEl.getAttribute('data-css');
      var css = decodeURIComponent(escape(atob(cssEncoded)));
      document.getElementById('app-styles').textContent = css;
    })();

    // Orbit SDK - music/apps are no-ops in standalone mode, but keep the same
    // method names as the in-emulator SDK so apps don't crash.
    (function() {
      var noop = function() { console.warn('[orbit] disabled in standalone mode'); };
      var storeData = {};
      try {
        storeData = JSON.parse(decodeURIComponent(escape(atob(document.getElementById('app-code').getAttribute('data-store'))))) || {};
      } catch (e) { storeData = {}; }

      window.orbit = {
        music: {
          play: noop, pause: noop, toggle: noop, next: noop, prev: noop,
          playTrack: noop,
          getCurrentTrack: function() { return null; },
          isPlaying: function() { return false; }
        },
        apps: {
          openMusicPlayer: noop, openPhotoBooth: noop, openGarden: noop,
          openGuestbook: noop, openOrbitTV: noop, openGenius: noop,
          openFileBrowser: noop
        },
        user: { handle: null, isLoggedIn: false },
        notify: function(msg) { console.log('[orbit notification]', msg); },
        storage: {
          get: function(key) {
            return Object.prototype.hasOwnProperty.call(storeData, key) ? storeData[key] : null;
          },
          set: function(key, value) {
            storeData[key] = value;
            window.parent.postMessage({ type: 'sdk', action: 'storage.set', data: { key: key, value: value } }, '*');
          },
          clear: function() {
            storeData = {};
            window.parent.postMessage({ type: 'sdk', action: 'storage.clear' }, '*');
          }
        }
      };
    })();

    // Decode and compile user code with Babel
    (function() {
      var codeEl = document.getElementById('app-code');
      var codeEncoded = codeEl.getAttribute('data-code');
      var userCode = decodeURIComponent(escape(atob(codeEncoded)));

      // Strip import statements - React is loaded via CDN
      userCode = userCode.replace(/^\\s*import\\s+.*?from\\s+['"].*?['"];?\\s*$/gm, '');
      userCode = userCode.replace(/^\\s*import\\s+['"].*?['"];?\\s*$/gm, '');
      // Strip export statements - not needed for eval
      userCode = userCode.replace(/^\\s*export\\s+default\\s+/gm, '');
      userCode = userCode.replace(/^\\s*export\\s+/gm, '');

      // Prepend React hooks destructuring so they're available in scope
      var hooksPreamble = 'const { useState, useEffect, useRef, useCallback, useMemo, useContext, useReducer, useLayoutEffect, useId } = React;\\n' +
        'const { createContext, forwardRef, memo, Fragment, createElement } = React;\\n\\n';

      // Add render call to user code
      var fullCode = hooksPreamble + userCode + '\\n\\n' +
        'if (typeof App !== "undefined") {' +
        '  ReactDOM.createRoot(document.getElementById("root")).render(React.createElement(App, { orbit: window.orbit }));' +
        '} else {' +
        '  document.getElementById("root").innerHTML = "<div style=\\'padding: 20px; color: #ff6b6b;\\'>Error: No App component found</div>";' +
        '}';

      try {
        var compiled = Babel.transform(fullCode, { presets: [['react', { runtime: 'classic' }]], sourceType: 'script' }).code;
        eval(compiled);
      } catch(e) {
        console.error('Compilation error:', e);
        document.getElementById('root').innerHTML = '<div style="padding: 20px; color: #ff6b6b;"><strong>Error:</strong> ' + e.message + '</div>';
      }
    })();
  <` + `/script>
</body>
</html>`;

    // html-v2: inject the shim right after <head> so it runs before app scripts
    let iframeContent: string;
    if (app.format === 'html-v2' && app.html) {
      const headMatch = app.html.match(/<head[^>]*>/i);
      if (headMatch && headMatch.index !== undefined) {
        const insertAt = headMatch.index + headMatch[0].length;
        iframeContent = app.html.slice(0, insertAt) + standaloneShim + app.html.slice(insertAt);
      } else {
        iframeContent = standaloneShim + app.html;
      }
    } else {
      iframeContent = jsxContent;
    }

    // Load via srcdoc (sandboxed without allow-same-origin, contentDocument is inaccessible)
    const iframe = iframeRef.current;
    iframe.setAttribute('srcdoc', iframeContent);

    // Storage bridge: the parent page owns localStorage for the sandboxed app
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) return;
      const { type, action, data } = event.data || {};
      if (type !== 'sdk') return;

      if (action === 'storage.set') {
        try {
          localStorage.setItem(storagePrefix + data.key, JSON.stringify(data.value));
        } catch (e) {
          console.error('Storage error:', e);
        }
      } else if (action === 'storage.clear') {
        try {
          Object.keys(localStorage).forEach(key => {
            if (key.indexOf(storagePrefix) === 0) {
              localStorage.removeItem(key);
            }
          });
        } catch (e) {
          console.error('Storage error:', e);
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [app, appId]);

  if (loading) {
    return (
      <div className="standalone-loading">
        <div className="standalone-loading-spinner"></div>
        <p>Loading app...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="standalone-error">
        <div className="standalone-error-icon">🚫</div>
        <h1>App Not Found</h1>
        <p>{error}</p>
        <a href="/" className="standalone-back-link">← Go to in orbit</a>
      </div>
    );
  }

  if (!app) {
    return null;
  }

  return (
    <div className="standalone-container">
      <header className="standalone-header">
        <a href="/" className="standalone-logo">inorbit.fm</a>
        <div className="standalone-app-info">
          <span className="standalone-app-emoji">{app.emoji}</span>
          <span className="standalone-app-name">{app.name}</span>
          <span className="standalone-app-author">by @{app.authorHandle}</span>
        </div>
      </header>

      <main className="standalone-main">
        <iframe
          ref={iframeRef}
          className="standalone-iframe"
          title={app.name}
          sandbox={SANDBOX_PERMS}
        />
      </main>

      <footer className="standalone-footer">
        <span>Made with</span>
        <a href="/" className="standalone-footer-link">orbit terminal</a>
      </footer>
    </div>
  );
};

export default StandaloneApp;
